import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '~/lib/api-client';
import { prefetchSocialUrl, sendVerificationEmail, signUpEmail, useSession } from '~/lib/auth';
import { readStoredHandle } from '~/lib/handle';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/auth/sign-up')({
  head: () =>
    buildHead({
      title: 'Sign Up Free',
      description:
        'Create a free Prod Battle account. Unlimited matches, uploads, and voting. Sign up with email, Google, or Discord.',
      path: '/auth/sign-up',
    }),
  component: SignUp,
});

// ─── Guest-handle claim prompt ────────────────────────────────────────────────

// Auto-generated handles follow the pattern: <adjective>-<noun>-<3-digit-number>
// e.g. "gritty-808-336", "phonky-stoat-512". We use this to detect whether
// the stored handle looks like it was auto-generated (not user-chosen).
const GUEST_HANDLE_RE = /^[a-z]+-[a-z0-9]+-\d+$/;

type ClaimState = 'idle' | 'claiming' | 'done' | 'skipped';

function GuestHandleClaimCard({
  guestHandle,
  onDone,
}: {
  guestHandle: string;
  onDone: () => void;
}) {
  const [claimState, setClaimState] = useState<ClaimState>('idle');
  const [err, setErr] = useState<string | null>(null);

  async function onKeep() {
    setErr(null);
    setClaimState('claiming');
    try {
      await api('/me/claim-guest-handle', {
        method: 'POST',
        body: JSON.stringify({ guestHandle }),
      });
      // Clear the stored guest handle - it now belongs to the account.
      try {
        window.sessionStorage.removeItem('pb_handle');
      } catch {
        // sessionStorage unavailable - ignore.
      }
      setClaimState('done');
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setClaimState('skipped');
      // Fall through to "use a new one" flow on any error.
      onDone();
    }
  }

  function onSkip() {
    try {
      window.sessionStorage.removeItem('pb_handle');
    } catch {
      // ignore
    }
    setClaimState('skipped');
    onDone();
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-6">
      <header>
        <h2 className="text-2xl font-bold">Keep your guest history?</h2>
      </header>
      <p className="text-muted-foreground">
        You played a few rounds as <strong>{guestHandle}</strong>. Keep that handle and your match
        history?
      </p>
      {err && <p className="text-sm text-red-500">{err}</p>}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onKeep}
          disabled={claimState === 'claiming'}
          className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {claimState === 'claiming' ? 'Claiming...' : 'Keep handle'}
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={claimState === 'claiming'}
          className="flex-1 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Use a new one
        </button>
      </div>
    </section>
  );
}

function CheckYourEmail({ email }: { email: string }) {
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  async function onResend() {
    setResendState('sending');
    try {
      await sendVerificationEmail(email);
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-4 text-center">
      <h2 className="text-2xl font-bold">Check your email</h2>
      <p className="text-muted-foreground">
        We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account.
      </p>
      <p className="text-xs text-muted-foreground">
        No email after a minute? Check spam or{' '}
        {resendState === 'sent' ? (
          <span className="text-green-600">sent!</span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={resendState === 'sending'}
            className="underline disabled:opacity-50"
          >
            {resendState === 'sending' ? 'sending...' : 'resend'}
          </button>
        )}
        {resendState === 'error' && (
          <span className="ml-1 text-red-500">Could not resend. Try signing up again.</span>
        )}
      </p>
    </section>
  );
}

// Must match the server-side HANDLE_RE in prod-battle-api/src/routes/me.ts.
const HANDLE_RE = /^[a-zA-Z0-9_-]{3,20}$/;

function SignUp() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [handle, setHandle] = useState('');
  const [handleErr, setHandleErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  // Set to a guest handle string when the user is signed in and has a
  // claimable guest handle in sessionStorage. null = no claim prompt needed.
  const [pendingGuestHandle, setPendingGuestHandle] = useState<string | null>(null);
  const [oauthErr, setOauthErr] = useState<string | null>(null);
  const [oauthBusy, setOauthBusy] = useState<'google' | 'discord' | null>(null);
  // You must explicitly agree to the Terms + Privacy Policy before any
  // registration method is enabled. Required, not a passive notice.
  const [agreed, setAgreed] = useState(false);

  async function startSocial(provider: 'google' | 'discord') {
    if (!agreed) {
      setOauthErr('Please agree to the Terms of Service and Privacy Policy first.');
      return;
    }
    setOauthErr(null);
    setOauthBusy(provider);
    try {
      const url = await prefetchSocialUrl(provider, `${window.location.origin}/play`);
      window.location.href = url;
    } catch (e) {
      setOauthErr((e as Error).message);
      setOauthBusy(null);
    }
  }

  // When a session appears (either because verification wasn't required, or
  // because the user clicked the verification email and landed back on this
  // page with a fresh session cookie), check sessionStorage for a claimable
  // guest handle. We only show the prompt once: if it's already been acted
  // on, it won't be in sessionStorage anymore.
  //
  // The check runs after mount via the session hook. If session is already
  // present on first render (e.g. OAuth callback) we read it synchronously.
  if (session && pendingGuestHandle === null && typeof window !== 'undefined') {
    const stored = readStoredHandle();
    if (stored && GUEST_HANDLE_RE.test(stored)) {
      // Queue as a microtask so we don't mutate state during render.
      // We guard with === null to ensure this only fires once.
      setTimeout(() => setPendingGuestHandle(stored), 0);
    }
  }

  function validateHandle(value: string): boolean {
    if (!HANDLE_RE.test(value)) {
      setHandleErr('Handle can only contain letters, numbers, _ and - (3-20 characters).');
      return false;
    }
    setHandleErr(null);
    return true;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!agreed) {
      setErr('Please agree to the Terms of Service and Privacy Policy first.');
      return;
    }
    if (!validateHandle(handle)) return;
    setPending(true);
    try {
      const result = await signUpEmail({ email, password, name: handle });
      if (result.token === null) {
        // Email verification required - no session was created. Show the
        // "check your email" waiting screen instead of navigating to /play.
        setAwaitingVerification(true);
        return;
      }
      // Session created immediately (e.g. local dev without email verification).
      // Check for a claimable guest handle now.
      const stored = readStoredHandle();
      if (stored && GUEST_HANDLE_RE.test(stored)) {
        setPendingGuestHandle(stored);
        return;
      }
      // No guest handle to claim - go straight to /play.
      window.location.assign('/play');
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setPending(false);
    }
  }

  function onClaimDone() {
    setPendingGuestHandle(null);
    void navigate({ to: '/play' });
  }

  // Show the guest-handle claim prompt once the user is signed in and has a
  // claimable handle in sessionStorage (either set by the claim-trigger in
  // onSubmit or by the session-check above after email verification).
  if (pendingGuestHandle) {
    return <GuestHandleClaimCard guestHandle={pendingGuestHandle} onDone={onClaimDone} />;
  }

  if (awaitingVerification) {
    return <CheckYourEmail email={email} />;
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-6">
      <header>
        <h2 className="text-2xl font-bold">Create account</h2>
        <p className="text-muted-foreground text-sm">
          Already have one?{' '}
          <Link to="/auth/sign-in" className="underline">
            Sign in.
          </Link>
        </p>
      </header>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            if (e.target.checked) setOauthErr(null);
          }}
          className="mt-0.5 size-4 shrink-0 accent-primary"
        />
        <span className="text-muted-foreground">
          I agree to the{' '}
          <Link to="/legal" hash="terms" className="underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/legal" hash="privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </span>
      </label>

      <div className="grid gap-2">
        <button
          type="button"
          onClick={() => startSocial('google')}
          disabled={oauthBusy !== null || !agreed}
          className="w-full rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
        >
          {oauthBusy === 'google' ? 'Opening Google...' : 'Continue with Google'}
        </button>
        <button
          type="button"
          onClick={() => startSocial('discord')}
          disabled={oauthBusy !== null || !agreed}
          className="w-full rounded-md border border-border bg-[#5865F2] px-4 py-2 text-sm font-medium text-white hover:bg-[#4752C4] disabled:opacity-60"
        >
          {oauthBusy === 'discord' ? 'Opening Discord...' : 'Continue with Discord'}
        </button>
        {oauthErr && <p className="text-xs text-red-500 text-center">{oauthErr}</p>}
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        or
        <div className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Handle</span>
          <input
            type="text"
            required
            minLength={3}
            maxLength={20}
            value={handle}
            onChange={(e) => {
              setHandle(e.target.value);
              if (handleErr) validateHandle(e.target.value);
            }}
            onBlur={(e) => validateHandle(e.target.value)}
            placeholder="your-producer-alias"
            className={`w-full rounded-md border bg-background px-3 py-2 ${handleErr ? 'border-red-500' : 'border-border'}`}
          />
          {handleErr ? (
            <p className="mt-1 text-xs text-red-500">{handleErr}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              3-20 characters: letters, numbers, _ or -. No @ or spaces.
            </p>
          )}
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Password</span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        {err && <p className="text-sm text-red-500">{err}</p>}
        <button
          type="submit"
          disabled={pending || !agreed}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {pending ? 'Creating...' : 'Create account'}
        </button>
      </form>
    </section>
  );
}
